import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { OUTPUT_FORMAT_OPTIONS, TARGET_OS_OPTIONS } from '../../../types/velociraptor'
import type { CollectorSettings } from '../collector-settings-form.types'

const SELECT_CLASS =
  'w-full text-sm px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-[#4e5e82] focus:border-transparent'
const INPUT_CLASS = `${SELECT_CLASS} placeholder-slate-400`

interface UpdateFn {
  <K extends keyof CollectorSettings>(key: K, value: CollectorSettings[K]): void
}

interface OsAndOutputProps {
  settings: CollectorSettings
  update: UpdateFn
  t: TFunction<'cases'>
}

export function OsAndOutput({ settings, update, t }: OsAndOutputProps): ReactElement {
  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
          {t('velociraptor.configModal.targetOs', 'Target OS')}
        </label>
        <select
          value={settings.targetOs}
          onChange={(event) => {
            update('targetOs', event.target.value)
          }}
          className={SELECT_CLASS}
        >
          {TARGET_OS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
          {t('velociraptor.configModal.outputFormat', 'Output Format')}
        </label>
        <select
          value={settings.outputFormat}
          onChange={(event) => {
            update('outputFormat', event.target.value)
          }}
          className={SELECT_CLASS}
        >
          {OUTPUT_FORMAT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}

export function EncryptionField({ settings, update, t }: OsAndOutputProps): ReactElement {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
        {t('velociraptor.configModal.encryptionPassword', 'Encryption Password')}
      </label>
      <input
        type="password"
        value={settings.encryptionPassword}
        onChange={(event) => {
          update('encryptionPassword', event.target.value)
        }}
        placeholder={t(
          'velociraptor.configModal.encryptionPlaceholder',
          'Leave blank for no encryption'
        )}
        className={INPUT_CLASS}
      />
    </div>
  )
}

export function CompressionField({ settings, update, t }: OsAndOutputProps): ReactElement {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
        {t('velociraptor.configModal.compressionLevel', 'Compression Level')}
        <span className="ml-2 font-normal text-slate-400">({settings.compressionLevel})</span>
      </label>
      <input
        type="range"
        min={0}
        max={9}
        value={settings.compressionLevel}
        onChange={(event) => {
          update('compressionLevel', Number.parseInt(event.target.value, 10))
        }}
        className="w-full accent-[#4e5e82]"
      />
      <div className="mt-0.5 flex justify-between text-xs text-slate-400">
        <span>{t('velociraptor.configModal.none', 'None')}</span>
        <span>{t('velociraptor.configModal.maximum', 'Maximum')}</span>
      </div>
    </div>
  )
}

export function ResourceLimits({ settings, update, t }: OsAndOutputProps): ReactElement {
  return (
    <div>
      <p className="mb-2 text-xs font-medium text-slate-700 dark:text-slate-300">
        {t('velociraptor.configModal.resourceLimits', 'Resource Limits')}
      </p>
      <div className="grid grid-cols-2 gap-4">
        <NumberField
          label={t('velociraptor.configModal.cpuLimit', 'CPU Limit (%)')}
          value={settings.cpuLimit}
          max={100}
          placeholder="0 = unlimited"
          onChange={(value) => {
            update('cpuLimit', value)
          }}
        />
        <NumberField
          label={t('velociraptor.configModal.iopsLimit', 'IOPS Limit')}
          value={settings.iopsLimit}
          placeholder="0 = unlimited"
          onChange={(value) => {
            update('iopsLimit', value)
          }}
        />
      </div>
    </div>
  )
}

export function TimeoutsBlock({ settings, update, t }: OsAndOutputProps): ReactElement {
  return (
    <div>
      <p className="mb-2 text-xs font-medium text-slate-700 dark:text-slate-300">
        {t('velociraptor.configModal.timeouts', 'Timeouts (seconds)')}
      </p>
      <div className="grid grid-cols-2 gap-4">
        <NumberField
          label={t('velociraptor.configModal.progressTimeout', 'Progress Timeout')}
          value={settings.progressTimeout}
          onChange={(value) => {
            update('progressTimeout', value)
          }}
        />
        <NumberField
          label={t('velociraptor.configModal.overallTimeout', 'Overall Timeout')}
          value={settings.timeout}
          onChange={(value) => {
            update('timeout', value)
          }}
        />
      </div>
    </div>
  )
}

interface NumberFieldProps {
  label: string
  value: number
  onChange: (value: number) => void
  max?: number
  placeholder?: string
}

function NumberField({ label, value, onChange, max, placeholder }: NumberFieldProps): ReactElement {
  return (
    <div>
      <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">{label}</label>
      <input
        type="number"
        min={0}
        max={max}
        value={value}
        placeholder={placeholder}
        onChange={(event) => {
          onChange(Number.parseInt(event.target.value, 10) || 0)
        }}
        className={INPUT_CLASS}
      />
    </div>
  )
}
